import { useQuery } from "@tanstack/react-query";
import {
  Boxes,
  Home,
  Package,
  Settings,
  LucideIcon,
  PanelBottomOpen,
} from "lucide-react";
import { NavLink } from "react-router-dom";

import { useSidebar } from "@/components/ui/sidebar";
import { ROUTES, TableURL } from "@/constants/urls";
import { cn } from "@/lib/utils";
import { listCurrentOrders } from "@/services/api/order/actions";

const MAX_ORDERS = 20;

interface BottomNavItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: React.ReactNode;
}

const BottomNavItem = ({
  to,
  icon: Icon,
  label,
  badge,
}: BottomNavItemProps) => {
  const { setOpenMobile } = useSidebar();

  const handleClick = () => {
    setOpenMobile(false);
  };

  return (
    <NavLink
      to={to}
      onClick={handleClick}
      className={({ isActive }) =>
        cn(
          "relative flex flex-col items-center justify-center px-2 py-1 text-xs",
          {
            "text-primary": isActive,
            "text-muted-foreground hover:text-foreground": !isActive,
          }
        )
      }
    >
      {({ isActive }) => (
        <>
          <div className="relative">
            <Icon className="mb-1 size-5" />
            {badge}
          </div>
          <span>{label}</span>
          {isActive && (
            <span className="bg-primary absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full" />
          )}
        </>
      )}
    </NavLink>
  );
};

const OrdersBadge = () => {
  const { data: orders } = useQuery({
    queryKey: ["order", "list", "current"],
    queryFn: listCurrentOrders,
  });

  if (!orders || orders.count === 0) return null;

  const isOverMax = orders.count >= MAX_ORDERS;

  return (
    <div className="bg-secondary text-secondary-foreground absolute -top-1 left-2 flex h-4 w-fit items-center justify-center rounded-full p-1 text-xs font-medium">
      {isOverMax ? `${MAX_ORDERS}+` : orders.count}
    </div>
  );
};

export default function MobileBottomNav() {
  const { setOpenMobile } = useSidebar();

  return (
    <div className="border-border bg-background shrink-0 border-t md:hidden">
      <nav className="flex items-center justify-around px-2 pt-1 pb-[calc(0.25rem+env(safe-area-inset-bottom,0px))]">
        <BottomNavItem to={`/${ROUTES.DASHBOARD}`} icon={Home} label="Home" />

        <BottomNavItem
          to={`/${TableURL.CURRENT}/${TableURL.ORDERS}`}
          icon={Package}
          label="Orders"
          badge={<OrdersBadge />}
        />

        {/*
          The word is the control, same as Home and Orders. A label outside
          the button long-presses into a selection and the tap only hits the
          icon. Inside the button it gets touch-action: manipulation,
          user-select: none, and the press scale.
        */}
        <button
          type="button"
          onClick={() => setOpenMobile(true)}
          className="text-muted-foreground hover:text-foreground relative flex flex-col items-center justify-center px-2 py-1 text-xs"
        >
          <PanelBottomOpen className="mb-1 size-5" />
          <span>Menu</span>
        </button>

        <BottomNavItem
          to={`/${TableURL.CURRENT}/${TableURL.STOCK}`}
          icon={Boxes}
          label="Stock"
        />

        <BottomNavItem
          to={`/${ROUTES.SETTINGS}`}
          icon={Settings}
          label="Settings"
        />
      </nav>
    </div>
  );
}
